import { type FC, useCallback } from "react";

import { cx } from "@emotion/css";
import { Dropdown, PlacementTypes } from "@snapchat/snap-design-system";

import { type Visibility } from "../../gql-generated/types";
import { buttonDisabled } from "../../shared/styles";
import { type DisabledVisibilityOptionConfig } from "../hooks/visibilityConfigs";

import { VisibilityMenu } from "./LensVisibility.styles";
import { visibilityTrigger } from "./LensVisibilityCompact.styles";
import { LensVisibilityLabelCompact } from "./LensVisibilityLabelCompact";
import { VISIBILITY_OPTION_MAP } from "./constants";

interface LensVisibilityCompactProps {
    visibility: Visibility;
    setVisibility: (value: Visibility) => void;
    loading?: boolean;
    disabled?: boolean;
    options: Visibility[];
    disabledOptions?: Partial<Record<Visibility, DisabledVisibilityOptionConfig>>;
}

export const LensVisibilityCompact: FC<LensVisibilityCompactProps> = ({
    visibility,
    setVisibility,
    loading = false,
    disabled = false,
    options,
    disabledOptions,
}) => {
    const makeSetVisibility = useCallback(
        (value: Visibility) => () => {
            setVisibility(value);
        },
        [setVisibility],
    );

    return (
        <Dropdown
            placement={PlacementTypes.BOTTOM_RIGHT}
            className={cx(visibilityTrigger, disabled && buttonDisabled)}
            buttonProps={{
                label: <LensVisibilityLabelCompact loading={loading} disabled={disabled} visibility={visibility} />,
            }}
            isDisabled={loading || disabled}
            overlay={
                <VisibilityMenu>
                    {options.map((option) => {
                        const VisibilityOption = VISIBILITY_OPTION_MAP[option];
                        const isSelected = option === visibility;
                        const disabledVisibilityOptionConfig = disabledOptions?.[option];
                        return (
                            <VisibilityOption
                                key={option}
                                setVisibility={makeSetVisibility(option)}
                                selected={isSelected}
                                disabledVisibilityOptionConfig={disabledVisibilityOptionConfig}
                            />
                        );
                    })}
                </VisibilityMenu>
            }
        />
    );
};
